import api from '@api/apiServices';
import { ColumnType, DynamicTable } from '@Components/General/DynamicTable';
import { StatusField } from '@Components/General/DynamicTable/Cells';
import NoDataComponent from '@Components/General/Feedback/no-data-component';
import QuickOptions, { QuickOption } from '@Components/General/QuickOptions';
import TableSkeleton from '@Components/General/Skeletons/table';
import useDialog from '@hooks/useDialog';
import {
    CloseRounded,
    DeleteOutlineRounded,
    EditRounded,
} from '@mui/icons-material';
import { LoadingButton } from '@mui/lab';
import {
    Box,
    Button,
    IconButton,
    TableCell,
    TableRow,
    TextField,
} from '@mui/material';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { closeOptions } from '@utils/utils';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { Trans, useTranslation } from 'react-i18next';

const emailDefaultValues = { name: '', reply_to: '' };

const EditEmail = ({ email = emailDefaultValues, onClose }) => {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const isNew = !email?.id;
    const { mutate, isPending } = useMutation({
        mutationKey: isNew ? ['addSingleEmail'] : ['updateSingleEmail'],
        onSuccess: () => {
            queryClient.invalidateQueries({ queryKey: ['singleEmails'] });
            onClose();
        },
    });
    const { register, getValues } = useForm({
        defaultValues: {
            name: email.name,
            reply_to: email.reply_to,
        },
    });

    return (
        <TableRow>
            {isNew ? (
                <>
                    <TableCell />
                    <TableCell padding="checkbox">
                        <TextField
                            fullWidth
                            size="small"
                            placeholder={t('Email')}
                            {...register('email')}
                        />
                    </TableCell>
                </>
            ) : (
                <>
                    <TableCell>{email.status}</TableCell>
                    <TableCell>{email.email}</TableCell>
                </>
            )}
            <TableCell padding="checkbox">
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('Sender Name')}
                    {...register('name')}
                />
            </TableCell>
            <TableCell padding="checkbox">
                <TextField
                    fullWidth
                    size="small"
                    placeholder={t('Reply To Email')}
                    {...register('reply_to')}
                />
            </TableCell>
            <TableCell align="right">
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'flex-end',
                    }}
                >
                    <IconButton size="medium" onClick={onClose}>
                        <CloseRounded />
                    </IconButton>
                    <LoadingButton
                        variant="filled"
                        size="medium"
                        loading={isPending}
                        onClick={() =>
                            mutate(
                                isNew ? getValues() : [email.id, getValues()],
                            )
                        }
                    >
                        {t('Save')}
                    </LoadingButton>
                </Box>
            </TableCell>
        </TableRow>
    );
};

const SingleEmails = () => {
    const { t } = useTranslation();
    const [edit, setEdit] = useState(null);
    const dialog = useDialog();
    const {
        data: emails,
        isLoading,
        refetch,
    } = useQuery({
        queryKey: ['singleEmails'],
        queryFn: () => api.settings.emails['single-emails'].get(),
        initialData: [],
    });

    const deleteEmailMutation = useMutation({
        mutationKey: ['deleteSingleEmail'],
        onSuccess: () => {
            refetch();
            dialog.close();
            closeOptions(dialog);
        },
    });

    const getQuickOptions = (row): QuickOption[] => [
        {
            label: t('Edit'),
            onClick: () => setEdit(row.id),
            Icon: EditRounded,
        },
        {
            label: t('Delete'),
            onClick: () =>
                dialog.open({
                    title: t('Delete Email'),
                    content: (
                        <Trans t={t} values={{ email: row.email }}>
                            prompts:content.deleteSendingEmail
                        </Trans>
                    ),
                    onConfirm: () => row.id,
                    confirmMutation: deleteEmailMutation,
                    confirmLabel: t('Delete'),
                    confirmButtonProps: { color: 'error' },
                }),
            Icon: DeleteOutlineRounded,
            sx: { color: 'error.main' },
        },
    ];

    const columns: ColumnType[] = [
        {
            name: t('Status'),
            field: 'status',
            headCellProps: { sx: { width: 100 } },
            Component: StatusField,
        },
        {
            name: t('Email'),
            field: 'email',
            headCellProps: { width: 160 },
        },
        {
            name: t('Sender Name'),
            field: 'name',
            headCellProps: { width: 160 },
        },
        {
            name: t('Reply To Email'),
            field: 'reply_to',
            headCellProps: { width: 160 },
        },
        {
            name: '',
            field: '',
            renderField: row => (
                <Box display="flex" justifyContent="flex-end">
                    <QuickOptions options={getQuickOptions(row)} />
                </Box>
            ),
            rowCellProps: {
                align: 'right',
                padding: 'checkbox',
            },
            headCellProps: { width: 80 },
        },
    ];

    if (isLoading) return <TableSkeleton columns={4} />;

    if (!emails.length && !edit)
        return (
            <NoDataComponent
                primary={t('prompts:noData.senderEmails.primary')}
                secondary={t('prompts:noData.senderEmails.secondary')}
                action={
                    <Button
                        color="secondary"
                        variant="tonal"
                        onClick={() => setEdit('new')}
                    >
                        {t('Add Email')}
                    </Button>
                }
            />
        );
    return (
        <>
            <DynamicTable
                columns={columns}
                rows={emails}
                tableFooter={
                    <Box display="flex" justifyContent="flex-end">
                        <Button
                            color="secondary"
                            variant="tonal"
                            onClick={() => setEdit('new')}
                        >
                            {t('Add Email')}
                        </Button>
                    </Box>
                }
                renderRow={(row, tableRowComponent) =>
                    edit === row.id ? (
                        <EditEmail email={row} onClose={() => setEdit(null)} />
                    ) : (
                        tableRowComponent
                    )
                }
                extraRows={
                    edit === 'new' && (
                        <EditEmail onClose={() => setEdit(null)} />
                    )
                }
            />
        </>
    );
};

export default SingleEmails;
